import React from 'react';
import { Box, Card, Typography, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import { formatCurrency, formatNumber, formatPercentage } from '../utils/formatters';

interface StatsCardProps {
  title: string;
  value: number;
  prefix?: string;
  suffix?: string;
  change?: number;
  changeText?: string;
  icon: React.ReactElement;
  delay?: number;
  color?: 'primary' | 'secondary' | 'success' | 'error';
}

const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  prefix = '',
  suffix = '',
  change,
  changeText = 'vs last period',
  icon,
  delay = 0,
  color = 'primary',
}) => {
  const theme = useTheme();
  
  const colorMap = {
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
    success: theme.palette.success.main,
    error: theme.palette.error.main,
  };

  const gradientMap = {
    primary: 'linear-gradient(135deg, #af2326 0%, #c94144 100%)',
    secondary: 'linear-gradient(135deg, #ffffff 0%, #e6e6e6 100%)',
    success: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
    error: 'linear-gradient(135deg, #ff5252 0%, #ff7b7b 100%)',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      whileHover={{ y: -5 }}
    >
      <Card
        sx={{
          p: 3,
          height: '100%',
          position: 'relative',
          overflow: 'visible',
          background: 'rgba(28, 24, 25, 0.85)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          borderRadius: '20px',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: `0 8px 32px ${colorMap[color]}30`,
            border: `1px solid ${colorMap[color]}20`,
            '& .stats-icon': {
              transform: 'rotate(360deg)',
            },
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: gradientMap[color],
            opacity: 0.8,
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                fontSize: '0.875rem',
                mb: 1,
              }}
            >
              {title}
            </Typography>
            
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontSize: '2rem',
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                background: gradientMap[color],
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
              }}
            >
              {prefix === '$' ? formatCurrency(value) : formatNumber(value)}
            </Typography>

            {change !== undefined && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {change >= 0 ? (
                  <TrendingUp sx={{ fontSize: 16, color: theme.palette.success.main }} />
                ) : (
                  <TrendingDown sx={{ fontSize: 16, color: theme.palette.error.main }} />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: change >= 0 ? theme.palette.success.main : theme.palette.error.main,
                    fontWeight: 600,
                  }}
                >
                  {formatPercentage(change)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
                  {changeText}
                </Typography>
              </Box>
            )}
          </Box>

          <motion.div
            className="stats-icon"
            style={{
              transition: 'transform 0.6s ease',
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '16px',
                background: gradientMap[color],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 4px 20px ${colorMap[color]}40`,
                '& svg': {
                  fontSize: 28,
                  color: 'white',
                },
              }}
            >
              {icon}
            </Box>
          </motion.div>
        </Box>

        <motion.div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '40px',
            background: `linear-gradient(180deg, transparent 0%, ${colorMap[color]}10 100%)`,
            borderRadius: '0 0 20px 20px',
            opacity: 0,
          }}
          animate={{
            opacity: [0, 0.5, 0],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </Card>
    </motion.div>
  );
};

export default StatsCard;